import { HeroProductCard } from '@dfh-corp-sites/ui'
import { cn } from '@dfh-corp-sites/ui/utils'
import { bind, Subscribe } from '@react-rxjs/core'
import React from 'react'
import { Observable, of } from 'rxjs'

import { SanityDispensaryBio, SanityImage } from '../../../graphql/gatsby'
import {
  SearchParentProductBySkuDocument,
  SearchParentProductBySkuQuery,
} from '../../../graphql/search'
import useIsSsr from '../../../hooks/useIsSsr'
import { useLogAndCaptureLeftResult } from '../../../hooks/useLogAndCaptureLeftResult'
import { useDispensaryContext } from '../../../lib/dispensaries'
import { useSanityDispensaryBySlug } from '../../../lib/dispensaries/hooks/useSanityDispensaryBySlug'
import { FetchResult } from '../../../lib/fetch'
import { dispensaryImageUrlFor, useSanityRoleBasedContent } from '../../../lib/sanity'
import useSanityImage from '../../../lib/sanity/hooks/useSanityImage'
import { latestDispensaryContact$ } from '../../../lib/sanity-dispensaries/contact'
import { commaQueryFactory } from '../../../lib/search/query'
import { right } from '../../../utils/result'
import { mapRight } from '../../../utils/rx/operators'
import Link from '../../global/Link'
import Spinner from '../../Spinner'

export interface VirtualDispensaryProps extends SanityDispensaryBio {
  className?: string
  sideImage?: SanityImage
}

type BannerTypes = {
  backgroundColor: string
  name: string
  img: string
  sku: string
}

type ProductBySku = SearchParentProductBySkuQuery['parentProductBySku']

const Container = ({ children }: { children: React.ReactNode }) => {
  return <div className="px-3.5 md:px-8 xl:px-14 mx-auto">{children}</div>
}

const productBySkuQuery = commaQueryFactory(SearchParentProductBySkuDocument)

const getProductBySku = ({ sku }: { sku?: string | null }) =>
  !sku
    ? of({ _tag: 'Right' as const, data: null })
    : productBySkuQuery({
        variables: { sku, includeLabel: false, labelName: '' },
      }).pipe(
        mapRight(({ data }) => {
          return right(data?.parentProductBySku)
        }),
      )

const [useSearchRecommendedProducts] = bind(
  (sku: string | null): Observable<FetchResult<ProductBySku | null>> => {
    const response = getProductBySku({ sku })
    return response
  },
)

const Banner = ({ backgroundColor, img, name, sku }: BannerTypes) => {
  return (
    <div className="mt-12 w-full">
      <HeroProductCard
        backgroundColor={backgroundColor}
        image={{ src: img }}
        title={name}
        isSingleCard
        cta={<Link to={`/products/${sku}/`}>Learn More</Link>}
      />
    </div>
  )
}

const CustomBanner = () => {
  const dispensary = useDispensaryContext()

  const sanityDispensaryResult = useSanityDispensaryBySlug(dispensary?.slug)

  useLogAndCaptureLeftResult(sanityDispensaryResult)

  const dispensaryResult =
    sanityDispensaryResult._tag === 'Right' ? sanityDispensaryResult.data : undefined

  const skus = dispensaryResult?.homePage?.customBannerProducts ?? []
  const bannerBackground = dispensaryResult?.homePage?.customBannerColor
  const bannerImageRef = dispensaryResult?.homePage?.promotionalBanner?._ref ?? ''
  const isCustomBanner = dispensaryResult?.homePage?.isCustomBanner

  const productResult = useSearchRecommendedProducts(skus[0])
  const secondProduct = useSearchRecommendedProducts(skus[1])

  const productData = productResult._tag === 'Right' ? productResult.data : undefined
  const secondProductData = secondProduct._tag === 'Right' ? secondProduct.data : undefined

  const sanityImageUrl = useSanityImage(
    { sourceJson: productData?.mainImage?.json || undefined },
    {
      dpr: 2,
      fit: 'max',
      width: 200,
    },
  )

  const secondSanityImageUrl = useSanityImage(
    { sourceJson: secondProductData?.mainImage?.json || undefined },
    {
      dpr: 2,
      fit: 'max',
      width: 200,
    },
  )

  const bannerImageRefValidity = bannerImageRef.startsWith('image-')

  if (!dispensary) {
    return null
  }

  if (!isCustomBanner && (!bannerImageRef || !bannerImageRefValidity)) return null

  return (
    <Container>
      {isCustomBanner ? (
        <div>
          {secondProductData ? (
            <div className={cn(['flex flex-col gap-4', 'lg:flex-row'])}>
              <Banner
                backgroundColor={bannerBackground ?? ''}
                img={sanityImageUrl ?? ''}
                name={productData?.name ?? ''}
                sku={productData?.urlKey ?? ''}
              />

              <Banner
                backgroundColor={bannerBackground ?? ''}
                img={secondSanityImageUrl ?? ''}
                name={secondProductData?.name ?? ''}
                sku={secondProductData?.urlKey ?? ''}
              />
            </div>
          ) : (
            <Banner
              backgroundColor={bannerBackground ?? ''}
              img={sanityImageUrl ?? ''}
              name={productData?.name ?? ''}
              sku={productData?.urlKey ?? ''}
            />
          )}
        </div>
      ) : (
        <img
          className={cn('w-full object-cover rounded-lg', 'mt-10')}
          alt="Banner"
          sizes="(min-width: 768px) 770px, (min-width: 1280px) 1328px, 350px"
          srcSet={[
            `${
              dispensaryImageUrlFor({ _ref: bannerImageRef })
                .width(344)
                .height(116)
                .fit('max')
                .auto('format')
                .url() || ''
            } 350w`,
            `${
              dispensaryImageUrlFor({ _ref: bannerImageRef })
                .width(770)
                .height(260)
                .fit('max')
                .auto('format')
                .url() || ''
            } 770w`,
            `${
              dispensaryImageUrlFor({ _ref: bannerImageRef })
                .width(1328)
                .height(449)
                .fit('max')
                .auto('format')
                .url() || ''
            } 1328w`,
          ].join(', ')}
          src={
            dispensaryImageUrlFor({ _ref: bannerImageRef })
              .width(1328)
              .fit('max')
              .auto('format')
              .url() || ''
          }
        />
      )}
    </Container>
  )
}

const DispensaryBanner: React.FC<VirtualDispensaryProps> = ({ roleBasedContent }) => {
  // Suspense not supported server-side yet
  const isSsr = useIsSsr()
  const { shouldDisplay } = useSanityRoleBasedContent({ roleBasedContent })

  if (!shouldDisplay || isSsr) {
    return null
  }

  return (
    <Subscribe source$={latestDispensaryContact$} fallback={<Spinner loading />}>
      <CustomBanner />
    </Subscribe>
  )
}

export default DispensaryBanner
